(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var cleanups = new WeakMap();

  function elements(root, selector) {
    var list = [];
    if (root instanceof Element && root.matches(selector)) list.push(root);
    return list.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
  }

  function motionEnabled(element) {
    return element.getAttribute('data-animate-section') !== 'false' && !reducedMotion.matches;
  }

  function register(element, cleanup) {
    element.setAttribute('data-velloria-initialized', 'true');
    cleanups.set(element, cleanup || function () {});
  }

  function destroy(element) {
    var cleanup = cleanups.get(element);
    if (cleanup) cleanup();
    cleanups.delete(element);
    element.removeAttribute('data-velloria-initialized');
  }

  function initHero(hero) {
    if (hero.hasAttribute('data-velloria-initialized')) return;
    var visual = hero.querySelector('[data-hero-visual]');
    if (!visual) { register(hero); return; }
    var stage = hero.querySelector('[data-hero-stage]');

    var cleanupFns = [];
    if (motionEnabled(hero)) {
      requestAnimationFrame(function () { hero.classList.add('is-ready'); });
      if (stage) {
        hero.classList.add('is-stage-armed');
        var stageObserver = null;
        var stageImageTimer = 0;
        var stageImagesReady = false;
        var stageInView = false;
        var stageRevealed = false;
        var revealStage = function () {
          if (stageRevealed || !stageImagesReady || !stageInView) return;
          stageRevealed = true;
          requestAnimationFrame(function () {
            requestAnimationFrame(function () { hero.classList.add('is-stage-ready'); });
          });
        };
        var stageImages = Array.prototype.slice.call(stage.querySelectorAll('img'));
        var imagePromises = stageImages.map(function (image) {
          if (image.complete) return Promise.resolve();
          return new Promise(function (resolve) {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        });
        Promise.all(imagePromises).then(function () {
          stageImagesReady = true;
          revealStage();
        });
        stageImageTimer = window.setTimeout(function () {
          stageImagesReady = true;
          revealStage();
        }, 2600);
        if ('IntersectionObserver' in window) {
          var stageThreshold = window.matchMedia('(max-width: 900px)').matches ? .45 : .18;
          stageObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              var playing = entry.isIntersecting && entry.intersectionRatio >= .08;
              hero.classList.toggle('is-stage-in-view', playing);
              stageInView = entry.isIntersecting && entry.intersectionRatio >= stageThreshold;
              revealStage();
            });
          }, { rootMargin: '0px 0px -4% 0px', threshold: [0, .08, stageThreshold] });
          stageObserver.observe(visual);
        } else {
          stageInView = true;
          hero.classList.add('is-stage-in-view');
          revealStage();
        }
        cleanupFns.push(function () {
          stageRevealed = true;
          if (stageObserver) stageObserver.disconnect();
          if (stageImageTimer) window.clearTimeout(stageImageTimer);
        });
      }
    } else {
      hero.classList.add('is-static', 'is-stage-ready');
    }

    if (motionEnabled(hero)) {
      var scrollFrame = 0;
      var renderScroll = function () {
        scrollFrame = 0;
        var rect = hero.getBoundingClientRect();
        var distance = Math.max(1, hero.offsetHeight * .72);
        var progress = Math.max(0, Math.min(1, -rect.top / distance));
        visual.style.setProperty('--hero-scroll-rx', (-.45 * progress).toFixed(2) + 'deg');
        visual.style.setProperty('--hero-scroll-ry', (.8 * progress).toFixed(2) + 'deg');
        visual.style.setProperty('--hero-scroll-pan-x', (-3 * progress).toFixed(2) + 'px');
        visual.style.setProperty('--hero-scroll-pan-y', (-6 * progress).toFixed(2) + 'px');
        visual.style.setProperty('--hero-scroll-shadow-x', (2 * progress).toFixed(2) + 'px');
        visual.style.setProperty('--hero-scroll-product-x', (14 * progress).toFixed(2) + 'px');
        visual.style.setProperty('--hero-scroll-box-x', (-9 * progress).toFixed(2) + 'px');
        visual.style.setProperty('--hero-scroll-product-rx', (.65 * progress).toFixed(2) + 'deg');
        visual.style.setProperty('--hero-scroll-product-ry', (-3.6 * progress).toFixed(2) + 'deg');
        visual.style.setProperty('--hero-scroll-box-rx', (-.45 * progress).toFixed(2) + 'deg');
        visual.style.setProperty('--hero-scroll-box-ry', (2.5 * progress).toFixed(2) + 'deg');
      };
      var onScroll = function () {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(renderScroll);
      };
      renderScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      cleanupFns.push(function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        visual.style.removeProperty('--hero-scroll-rx');
        visual.style.removeProperty('--hero-scroll-ry');
        visual.style.removeProperty('--hero-scroll-pan-x');
        visual.style.removeProperty('--hero-scroll-pan-y');
        visual.style.removeProperty('--hero-scroll-shadow-x');
        visual.style.removeProperty('--hero-scroll-product-x');
        visual.style.removeProperty('--hero-scroll-box-x');
        visual.style.removeProperty('--hero-scroll-product-rx');
        visual.style.removeProperty('--hero-scroll-product-ry');
        visual.style.removeProperty('--hero-scroll-box-rx');
        visual.style.removeProperty('--hero-scroll-box-ry');
      });
    }

    if (motionEnabled(hero) && visual.hasAttribute('data-hero-tilt') && window.matchMedia('(pointer:fine)').matches) {
      var frame = 0;
      var nextX = 0;
      var nextY = 0;
      var nextPanX = 0;
      var nextPanY = 0;
      var nextLightX = 68;
      var nextLightY = 28;
      var nextShadowX = 0;
      var nextShadowScale = 1;
      var nextProductX = 0;
      var nextProductRX = 0;
      var nextProductRY = 0;
      var nextBoxX = 0;
      var nextBoxRX = 0;
      var nextBoxRY = 0;
      var motionScale = parseFloat(window.getComputedStyle(hero).getPropertyValue('--v-hero-motion')) || 1;
      var render = function () {
        frame = 0;
        visual.style.setProperty('--hero-rx', nextY.toFixed(2) + 'deg');
        visual.style.setProperty('--hero-ry', nextX.toFixed(2) + 'deg');
        visual.style.setProperty('--hero-pan-x', nextPanX.toFixed(2) + 'px');
        visual.style.setProperty('--hero-pan-y', nextPanY.toFixed(2) + 'px');
        visual.style.setProperty('--hero-light-x', nextLightX.toFixed(1) + '%');
        visual.style.setProperty('--hero-light-y', nextLightY.toFixed(1) + '%');
        visual.style.setProperty('--hero-shadow-x', nextShadowX.toFixed(2) + 'px');
        visual.style.setProperty('--hero-shadow-scale', nextShadowScale.toFixed(3));
        visual.style.setProperty('--hero-product-x', nextProductX.toFixed(2) + 'px');
        visual.style.setProperty('--hero-product-rx', nextProductRX.toFixed(2) + 'deg');
        visual.style.setProperty('--hero-product-ry', nextProductRY.toFixed(2) + 'deg');
        visual.style.setProperty('--hero-box-x', nextBoxX.toFixed(2) + 'px');
        visual.style.setProperty('--hero-box-rx', nextBoxRX.toFixed(2) + 'deg');
        visual.style.setProperty('--hero-box-ry', nextBoxRY.toFixed(2) + 'deg');
      };
      var onMove = function (event) {
        var rect = visual.getBoundingClientRect();
        var px = (event.clientX - rect.left) / rect.width - .5;
        var py = (event.clientY - rect.top) / rect.height - .5;
        nextX = Math.max(-1.8, Math.min(1.8, px * 3.6));
        nextY = Math.max(-1.2, Math.min(1.2, py * -2.4));
        nextPanX = px * -8;
        nextPanY = py * -5;
        nextLightX = 50 + px * 44;
        nextLightY = 42 + py * 34;
        nextShadowX = px * 10;
        nextShadowScale = 1 - Math.abs(px) * .07;
        nextProductX = px * 24 * motionScale;
        nextProductRX = py * -5 * motionScale;
        nextProductRY = px * 12 * motionScale;
        nextBoxX = px * -12 * motionScale;
        nextBoxRX = py * -2.5 * motionScale;
        nextBoxRY = px * -6 * motionScale;
        if (!frame) frame = requestAnimationFrame(render);
      };
      var onLeave = function () {
        nextX = 0;
        nextY = 0;
        nextPanX = 0;
        nextPanY = 0;
        nextLightX = 68;
        nextLightY = 28;
        nextShadowX = 0;
        nextShadowScale = 1;
        nextProductX = 0;
        nextProductRX = 0;
        nextProductRY = 0;
        nextBoxX = 0;
        nextBoxRX = 0;
        nextBoxRY = 0;
        if (!frame) frame = requestAnimationFrame(render);
      };
      visual.addEventListener('pointermove', onMove, { passive: true });
      visual.addEventListener('pointerleave', onLeave, { passive: true });
      cleanupFns.push(function () {
        visual.removeEventListener('pointermove', onMove);
        visual.removeEventListener('pointerleave', onLeave);
        if (frame) cancelAnimationFrame(frame);
        visual.style.removeProperty('--hero-rx');
        visual.style.removeProperty('--hero-ry');
        visual.style.removeProperty('--hero-pan-x');
        visual.style.removeProperty('--hero-pan-y');
        visual.style.removeProperty('--hero-light-x');
        visual.style.removeProperty('--hero-light-y');
        visual.style.removeProperty('--hero-shadow-x');
        visual.style.removeProperty('--hero-shadow-scale');
        visual.style.removeProperty('--hero-product-x');
        visual.style.removeProperty('--hero-product-rx');
        visual.style.removeProperty('--hero-product-ry');
        visual.style.removeProperty('--hero-box-x');
        visual.style.removeProperty('--hero-box-rx');
        visual.style.removeProperty('--hero-box-ry');
      });
    }

    register(hero, function () {
      cleanupFns.forEach(function (fn) { fn(); });
      hero.classList.remove('is-ready', 'is-static', 'is-stage-armed', 'is-stage-ready', 'is-stage-in-view');
    });
  }

  function initStory(story) {
    if (story.hasAttribute('data-velloria-initialized')) return;
    var frames = Array.prototype.slice.call(story.querySelectorAll('[data-story-frame]'));
    var steps = Array.prototype.slice.call(story.querySelectorAll('[data-story-step]'));
    if (!frames.length || !steps.length) { register(story); return; }

    var storyMotion = motionEnabled(story);
    if (storyMotion) story.classList.add('is-enhanced');
    var active = -1;
    var setActive = function (index) {
      index = Math.max(0, Math.min(index, steps.length - 1));
      if (index === active) return;
      active = index;
      frames.forEach(function (frame) {
        frame.classList.toggle('is-active', Number(frame.getAttribute('data-story-frame')) === index);
      });
      steps.forEach(function (step) {
        step.classList.toggle('is-active', Number(step.getAttribute('data-story-step')) === index);
      });
      story.style.setProperty('--story-progress', String((index + 1) / steps.length));
    };
    setActive(0);

    var observer = null;
    var storyBreakpoint = window.matchMedia('(min-width: 901px)');
    var bindObserver = function () {
      if (observer) observer.disconnect();
      observer = null;
      if (!storyMotion || !('IntersectionObserver' in window)) return;
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setActive(Number(entry.target.getAttribute('data-story-step')) || 0);
        });
      }, { rootMargin: storyBreakpoint.matches ? '-38% 0px -43% 0px' : '-44% 0px -54% 0px', threshold: 0 });
      steps.forEach(function (step) { observer.observe(step); });
    };
    bindObserver();
    var onStoryBreakpoint = function () { bindObserver(); };
    if (storyBreakpoint.addEventListener) storyBreakpoint.addEventListener('change', onStoryBreakpoint);
    else if (storyBreakpoint.addListener) storyBreakpoint.addListener(onStoryBreakpoint);

    var listeners = [];
    steps.forEach(function (step) {
      var activate = function () { setActive(Number(step.getAttribute('data-story-step')) || 0); };
      step.addEventListener('focus', activate);
      step.addEventListener('mouseenter', activate, { passive: true });
      listeners.push([step, activate]);
    });

    register(story, function () {
      if (observer) observer.disconnect();
      if (storyBreakpoint.removeEventListener) storyBreakpoint.removeEventListener('change', onStoryBreakpoint);
      else if (storyBreakpoint.removeListener) storyBreakpoint.removeListener(onStoryBreakpoint);
      listeners.forEach(function (pair) {
        pair[0].removeEventListener('focus', pair[1]);
        pair[0].removeEventListener('mouseenter', pair[1]);
      });
      story.classList.remove('is-enhanced');
    });
  }

  function initFormula(formula) {
    if (formula.hasAttribute('data-velloria-initialized')) return;
    var chapters = Array.prototype.slice.call(formula.querySelectorAll('[data-formula-chapter]'));
    var media = Array.prototype.slice.call(formula.querySelectorAll('[data-formula-media]'));
    if (!chapters.length) { register(formula); return; }

    var formulaMotion = motionEnabled(formula);
    if (formulaMotion) formula.classList.add('is-enhanced');
    var active = -1;
    var setActive = function (index) {
      index = Math.max(0, Math.min(index, chapters.length - 1));
      if (index === active) return;
      active = index;
      chapters.forEach(function (chapter) {
        chapter.classList.toggle('is-active', Number(chapter.getAttribute('data-formula-chapter')) === index);
      });
      media.forEach(function (item) {
        item.classList.toggle('is-active', Number(item.getAttribute('data-formula-media')) === index);
      });
      formula.setAttribute('data-tone', chapters[index].getAttribute('data-tone') || 'ivory');
    };
    setActive(0);

    var observer = null;
    var formulaBreakpoint = window.matchMedia('(max-width: 900px)');
    var bindFormulaObserver = function () {
      if (observer) observer.disconnect();
      observer = null;
      if (!formulaMotion || !('IntersectionObserver' in window)) return;
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setActive(Number(entry.target.getAttribute('data-formula-chapter')) || 0);
        });
      }, { rootMargin: formulaBreakpoint.matches ? '-44% 0px -54% 0px' : '-38% 0px -42% 0px', threshold: 0 });
      chapters.forEach(function (chapter) { observer.observe(chapter); });
    };
    bindFormulaObserver();
    var onFormulaBreakpoint = function () { bindFormulaObserver(); };
    if (formulaBreakpoint.addEventListener) formulaBreakpoint.addEventListener('change', onFormulaBreakpoint);
    else if (formulaBreakpoint.addListener) formulaBreakpoint.addListener(onFormulaBreakpoint);

    var listeners = [];
    chapters.forEach(function (chapter) {
      var activate = function () { setActive(Number(chapter.getAttribute('data-formula-chapter')) || 0); };
      chapter.addEventListener('focus', activate);
      chapter.addEventListener('mouseenter', activate, { passive: true });
      listeners.push([chapter, activate]);
    });

    register(formula, function () {
      if (observer) observer.disconnect();
      if (formulaBreakpoint.removeEventListener) formulaBreakpoint.removeEventListener('change', onFormulaBreakpoint);
      else if (formulaBreakpoint.removeListener) formulaBreakpoint.removeListener(onFormulaBreakpoint);
      listeners.forEach(function (pair) {
        pair[0].removeEventListener('focus', pair[1]);
        pair[0].removeEventListener('mouseenter', pair[1]);
      });
      formula.classList.remove('is-enhanced');
    });
  }

  function initDuo(duo) {
    if (duo.hasAttribute('data-velloria-initialized')) return;
    var observer = null;
    if (motionEnabled(duo) && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            duo.classList.add('is-in-view');
            observer.disconnect();
          }
        });
      }, { rootMargin: '0px 0px -12% 0px', threshold: .12 });
      observer.observe(duo);
    } else {
      duo.classList.add('is-in-view');
    }
    register(duo, function () { if (observer) observer.disconnect(); });
  }

  function init(root) {
    elements(root, '[data-v-hero]').forEach(initHero);
    elements(root, '[data-v-story]').forEach(initStory);
    elements(root, '[data-v-formula]').forEach(initFormula);
    elements(root, '[data-v-duo]').forEach(initDuo);
  }

  function destroyWithin(root) {
    elements(root, '[data-velloria-initialized]').forEach(destroy);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyWithin(event.target); });
  document.addEventListener('shopify:block:select', function (event) {
    var step = event.target.closest('[data-story-step]');
    if (step) step.focus({ preventScroll: true });
    var chapter = event.target.closest('[data-formula-chapter]');
    if (chapter) chapter.focus({ preventScroll: true });
  });
})();
